import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { MessageSquare, Send, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getEventChat, postEventChat } from '../apis/ride-24';

const POLL_INTERVAL_MS = 4000;

const apiError = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.message || fallback;

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const scrollChatToBottom = (el, behavior) => {
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior });
};

const EventChat = ({ eventId, user, canPost = true, disabledReason = null }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const scrollerRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  const fetchMessages = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await getEventChat(eventId, { skipLoader: silent });
      setMessages(data);
      setAccessError(null);
      const last = data[data.length - 1];
      if (last && last.id !== lastMessageIdRef.current) {
        lastMessageIdRef.current = last.id;
        // Scroll the message list only, so the page itself does not jump
        requestAnimationFrame(() => {
          scrollChatToBottom(scrollerRef.current, silent ? 'smooth' : 'auto');
        });
      }
    } catch (err) {
      if (!silent) {
        const detail = apiError(err, 'Failed to load chat');
        setAccessError(detail);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchMessages();
    const id = setInterval(() => fetchMessages({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchMessages]);

  const send = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const { data } = await postEventChat(eventId, { text: trimmed });
      setMessages((prev) => [...prev, data]);
      setText('');
      lastMessageIdRef.current = data.id;
      requestAnimationFrame(() => {
        scrollChatToBottom(scrollerRef.current, 'smooth');
      });
    } catch (err) {
      toast.error(apiError(err, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  if (accessError) {
    return (
      <Card className="bg-gray-900/80 border border-gray-800 shadow-xl text-gray-200" data-testid="chat-locked">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <MessageSquare className="w-5 h-5" /> Group Chat
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-400">{accessError}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="bg-gray-900/80 border border-gray-800 shadow-xl text-gray-200"
      data-testid="event-chat-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <MessageSquare className="w-5 h-5" /> Group Chat
          <span className="text-xs text-zinc-500 font-body ml-2">
            ({messages.length} message{messages.length === 1 ? '' : 's'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          ref={scrollerRef}
          className="max-h-96 overflow-y-auto pr-1 space-y-3 mb-4"
          data-testid="chat-messages"
        >
          {loading ? (
            <div className="text-sm text-zinc-500 text-center py-6">Loading chat…</div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-6" data-testid="chat-empty">
              No messages yet. Say hi to your fellow riders!
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.user_id === user.id;
              return (
                <div
                  key={m.id}
                  className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''}`}
                  data-testid={`chat-message-${m.id}`}
                >
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 ring-1 ring-zinc-700 flex-shrink-0 flex items-center justify-center">
                    {m.user_picture ? (
                      <img src={m.user_picture} alt={m.user_name} className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon className="w-4 h-4 text-zinc-400" />
                    )}
                  </div>
                  <div className={`max-w-[75%] ${mine ? 'text-right' : ''}`}>
                    <div className="text-xs text-zinc-500 mb-0.5">
                      <span className="text-zinc-300">{m.user_name}</span>
                      <span className="mx-1.5">·</span>
                      {formatTime(m.created_at)}
                    </div>
                    <div
                      className={`inline-block px-3 py-2 rounded-md text-sm whitespace-pre-wrap break-words ${
                        mine
                          ? 'bg-red-600/90 text-white'
                          : 'bg-zinc-800 text-zinc-100'
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {canPost ? (
          <form onSubmit={send} className="flex items-end gap-2" data-testid="chat-form">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(e);
                }
              }}
              placeholder="Type a message… (Shift+Enter for a new line)"
              rows={2}
              maxLength={2000}
              className="flex-1 resize-none rounded-sm bg-zinc-950 border border-zinc-800 hover:border-zinc-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-red-600 text-zinc-100 placeholder:text-zinc-500 px-3 py-2 text-sm"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!text.trim() || sending}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50 h-10"
              data-testid="chat-send-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        ) : (
          <div
            className="text-xs text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-3 text-center"
            data-testid="chat-post-disabled"
          >
            {disabledReason || 'RSVP as going to join this group chat.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EventChat;
